export const adminArticles = [
    {
        id: 'admin.overview',
        title: 'Admin Portal Overview',
        category: 'Platform Admin',
        order: 1,
        summary: 'Platform administration for super admins',
        content: `
The Admin Portal is available to platform super administrators. It provides tools for managing tenants, subscriptions, billing, and system health.

**Access:** Click your profile icon and select **Platform Admin** (only visible to super admins).

**Admin Sections:**

| Section | Purpose |
|---------|---------|
| **Dashboard** | Platform overview, tenant stats, system health |
| **Leads & Customers** | Manage sign-ups, trial tenants, and customer accounts |
| **Subscriptions** | View and manage active subscriptions |
| **License Plans** | Configure available subscription plans |
| **Coupons** | Create and manage discount codes |
| **Dev Tools** | Server controls and diagnostics |
    `,
        expandableSections: [
            {
                title: 'Accessing the Admin Portal',
                content: `
1. Log in as a super admin user
2. Click your profile icon in the top-right header
3. Select **Platform Admin**
4. The admin portal opens in a separate layout

**Note:** The admin portal operates outside of tenant context. You can manage all tenants from this single interface.

To return to the main app, use the **Back to App** button in the header.
        `,
            },
        ],
        keywords: ['admin', 'platform', 'super admin', 'management', 'portal'],
        route: '/admin',
        relatedArticles: ['admin.leads', 'admin.subscriptions', 'admin.plans'],
    },
    {
        id: 'admin.leads',
        title: 'Leads & Customers',
        category: 'Platform Admin',
        order: 2,
        summary: 'Managing sign-ups, trials, and customer accounts',
        content: `
The Leads & Customers section manages the full lifecycle from sign-up to active customer.

**Lead Stages:**

| Stage | Description |
|-------|-------------|
| **New** | Just signed up, email not verified |
| **Verified** | Email verified, tenant provisioned |
| **Trial** | Active trial period |
| **Active** | Paying customer |
| **Suspended** | Account suspended |
| **Churned** | Cancelled subscription |

**Key Actions:**

- View and filter leads by status
- Open lead details for full history
- Provision tenants manually
- Extend or modify trial periods
- View tenant schemas and usage
    `,
        expandableSections: [
            {
                title: 'Lead Detail View',
                content: `
Click a lead to open their detail panel:

- **Contact Info** - Email, name, company, phone
- **Status** - Current stage and history
- **Tenant** - Schema name, creation date, user count
- **Subscription** - Plan, billing status, payment history
- **Activity** - Login history, feature usage

**Actions Available:**
- Edit contact details
- Change lead status
- Reset password
- Impersonate user (for debugging)
- Delete tenant (destructive - requires confirmation)
        `,
            },
            {
                title: 'Manual Tenant Provisioning',
                content: `
If a user's verification email fails, you can manually provision their tenant:

1. Find the lead in the list
2. Open their detail view
3. Click **Provision Tenant**
4. The system creates:
   - Tenant record in the public schema
   - Dedicated schema with all tables
   - Default company settings
   - User account linked to the tenant
5. The user can now log in directly
        `,
            },
        ],
        keywords: ['leads', 'customers', 'sign-up', 'trial', 'provision', 'onboarding'],
        route: '/admin',
        relatedArticles: ['admin.overview', 'admin.subscriptions'],
    },
    {
        id: 'admin.subscriptions',
        title: 'Subscriptions Management',
        category: 'Platform Admin',
        order: 3,
        summary: 'Viewing and managing active subscriptions',
        content: `
The Subscriptions section shows all active and past subscriptions across the platform.

**Subscription Information:**

- Tenant name and owner
- Plan name and tier
- Billing cycle (monthly/annual)
- Status (active, past due, cancelled)
- Start date and renewal date
- Payment method

**Filtering:**

- Filter by status (active, trial, cancelled)
- Search by tenant name or email
- Sort by any column
    `,
        expandableSections: [
            {
                title: 'Subscription Actions',
                content: `
From the subscriptions list you can:

- **View Details** - Full subscription history
- **Change Plan** - Upgrade or downgrade
- **Cancel** - End the subscription
- **Extend Trial** - Add days to a trial period
- **View Invoices** - Stripe billing history
        `,
            },
        ],
        keywords: ['subscriptions', 'billing', 'plans', 'payment', 'renewal'],
        route: '/admin',
        relatedArticles: ['admin.overview', 'admin.plans'],
    },
    {
        id: 'admin.plans',
        title: 'License Plans',
        category: 'Platform Admin',
        order: 4,
        summary: 'Configuring subscription plans and pricing',
        content: `
Manage the subscription plans available to customers.

**Plan Configuration:**

- Plan name and description
- Monthly and annual pricing
- Feature inclusions (user limits, storage, etc.)
- Trial period settings
- Stripe product/price IDs

**Standard Plans:**

| Plan | Users | Features |
|------|-------|----------|
| Starter | 1-3 | Core BOQ, Orders, Contacts |
| Professional | 5-10 | All features, priority support |
| Enterprise | Unlimited | Custom features, dedicated support |
    `,
        expandableSections: [
            {
                title: 'Creating a Plan',
                content: `
1. Go to Admin > License Plans
2. Click **Add Plan**
3. Configure:
   - Plan name and description
   - Monthly price
   - Annual price (discounted)
   - User limit
   - Feature flags
4. Save the plan

**Note:** Plans sync with Stripe. Create the product and prices in Stripe first, then link them here.
        `,
            },
        ],
        keywords: ['plans', 'pricing', 'subscription', 'license', 'stripe'],
        route: '/admin',
        relatedArticles: ['admin.overview', 'admin.subscriptions', 'admin.coupons'],
    },
    {
        id: 'admin.coupons',
        title: 'Coupons & Discounts',
        category: 'Platform Admin',
        order: 5,
        summary: 'Creating and managing promotional discount codes',
        content: `
Create discount coupons for marketing promotions, partner deals, or customer retention.

**Coupon Types:**

| Type | Description |
|------|-------------|
| **Percentage** | e.g., 20% off for 3 months |
| **Fixed Amount** | e.g., $50 off first month |
| **Free Trial Extension** | e.g., 30 extra days trial |

**Coupon Properties:**

- Unique code (auto-generated or custom)
- Discount type and amount
- Duration (once, repeating, forever)
- Expiry date
- Usage limits (total and per-customer)
- Applicable plans
    `,
        expandableSections: [
            {
                title: 'Creating a Coupon',
                content: `
1. Go to Admin > Coupons
2. Click **Create Coupon**
3. Configure:
   - Code (or auto-generate)
   - Discount type and amount
   - Duration
   - Restrictions
4. Save

**Using Coupons:**
Customers enter the code during checkout. The discount is applied automatically through Stripe.
        `,
            },
        ],
        keywords: ['coupons', 'discounts', 'promo', 'marketing', 'promotions'],
        route: '/admin',
        relatedArticles: ['admin.plans', 'admin.subscriptions'],
    },
    {
        id: 'admin.devtools',
        title: 'Dev Tools',
        category: 'Platform Admin',
        order: 6,
        summary: 'Server controls and system diagnostics',
        content: `
Dev Tools provides system administration capabilities for monitoring and controlling the platform.

**Available Controls:**

- **API Health** - Check server status and response times
- **Cache Management** - View and clear server-side caches
- **Job Queue** - Monitor background jobs (OCR, cleanup, etc.)
- **Database** - Schema information and query stats

**Caution:** These tools can affect all tenants. Use carefully in production.
    `,
        keywords: ['dev tools', 'server', 'diagnostics', 'cache', 'health', 'admin'],
        route: '/admin',
        relatedArticles: ['admin.overview'],
    },
];
